import React from 'react';
import { Pressable, Text, View } from 'react-native';
import CutCard from '@/components/General/CutCard';
import DropdownMenu from '@/components/General/DropdownMenu';
import SolidButton from '@/components/General/SolidButton';
import Unicon from '@/components/General/Unicon';
import { serviceFlowStyles } from './serviceFlowStyles';

/**
 * Template library card (spec §3.2): name, optional description, item count,
 * scheduled span, separate event-default and location-override counts. Card itself opens the workspace;
 * row actions handle Duplicate / Archive / Restore.
 */
const ServiceFlowTemplateCard = ({
    template,
    onOpen,
    onDuplicate = null,
    onArchive = null,   // (template, archived) — parent confirms + mutates
    canManage = false,
    busy = false,
}) => {
    const spanLabel = template.span
        ? (() => {
            const fmt = (o) => o === 0 ? 'start' : `${Math.abs(o)}m ${o < 0 ? 'before' : 'after'}`;
            return `${fmt(template.span.startOffset)} – ${fmt(template.span.endOffset)}`;
        })()
        : 'No scheduled times';

    const menuOptions = [
        onDuplicate && { name: 'Duplicate', onSelect: () => onDuplicate(template) },
        onArchive && (template.isArchived
            ? { name: 'Restore', onSelect: () => onArchive(template, false) }
            : { name: 'Archive', isDestructive: true, onSelect: () => onArchive(template, true) }),
    ].filter(Boolean);

    return (
        <CutCard testID={`service-flow-template-card-${template.id}`} contentStyle={{ padding: 20 }}>
            <View>
                <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10 }}>
                    <View style={{ flex: 1 }}>
                        <Pressable onPress={() => onOpen(template)} disabled={busy} accessibilityRole="button">
                            <Text style={serviceFlowStyles.cardTitle} numberOfLines={1}>{template.name}</Text>
                        </Pressable>
                        {!!template.description && (
                            <Text style={serviceFlowStyles.cardDescription} numberOfLines={2}>{template.description}</Text>
                        )}
                    </View>
                    {menuOptions.length > 0 && canManage && (
                        <DropdownMenu
                            options={menuOptions}
                            disabled={busy}
                            triggerComponent={
                                <Pressable
                                    style={serviceFlowStyles.rowMenuButton}
                                    accessibilityLabel={`${template.name} options`}
                                    testID={`service-flow-template-menu-${template.id}`}
                                >
                                    <Unicon name="ellipsis-vertical" size={16} colorVariant="textSecondary" />
                                </Pressable>
                            }
                        />
                    )}
                </View>

                <View style={serviceFlowStyles.cardMetaRow}>
                    <Text style={serviceFlowStyles.cardMeta}>
                        {template.itemCount ?? (template.items?.length ?? 0)} item{(template.itemCount ?? template.items?.length ?? 0) === 1 ? '' : 's'}
                    </Text>
                    <Text style={serviceFlowStyles.cardMeta}>{spanLabel}</Text>
                    <Text style={serviceFlowStyles.cardMeta} testID={`service-flow-template-default-count-${template.id}`}>
                        {template.attachedEventCount ?? 0} event default{(template.attachedEventCount ?? 0) === 1 ? '' : 's'}
                    </Text>
                    <Text style={serviceFlowStyles.cardMeta} testID={`service-flow-template-location-count-${template.id}`}>
                        {template.attachedLocationCount ?? 0} location override{(template.attachedLocationCount ?? 0) === 1 ? '' : 's'}
                    </Text>
                </View>

                {canManage && (
                    <View style={serviceFlowStyles.cardActions}>
                        <SolidButton
                            size="sm"
                            type="events"
                            variant="outlined"
                            text="Open"
                            onPress={() => onOpen(template)}
                            disabled={busy}
                            testID={`service-flow-template-open-${template.id}`}
                        />
                    </View>
                )}
            </View>
        </CutCard>
    );
};

export default ServiceFlowTemplateCard;
